import Article, { PetProfileArticle, BeChewyArticle } from 'types/Articles';
import fetcher from '../util/fetcher';
import logger from '../util/log';
import { BLOG_URL } from '../util/config';
import { AnimalTypeId } from '../types/pet';
import { getImage } from '../util/articles';
const DOG_PET_ID = 1;
const CAT_PET_ID = 2;

const CAT_ARTICLE_PET_TYPE = 27753;
const DOG_ARTICLE_PET_TYPE = 27752;

const EXCLUDE_TAG = 27750;

type BlogPetTypeMapping = {
  [key: number]: number;
};

type BlogSpecialtyPetTypeMapping = {
  [key: number]: string;
};

const blogPetTypeMapping: BlogPetTypeMapping = {
  [CAT_PET_ID]: CAT_ARTICLE_PET_TYPE,
  [DOG_PET_ID]: DOG_ARTICLE_PET_TYPE,
};

const blogSpecialtyPetTypeMapping: BlogSpecialtyPetTypeMapping = {
  [AnimalTypeId.fish]: 'fish',
  [AnimalTypeId.bird]: 'bird',
  [AnimalTypeId.smallPet]: 'small-pet',
  [AnimalTypeId.reptile]: 'reptile',
  [AnimalTypeId.horse]: 'horse',
  [AnimalTypeId.farmAnimal]: '',
};

const PET_ARTICLES_MAX_AGE = 60 * 60 * 12 * 1000; // 12 hour intervals

const calculateCacheBusterParam = () =>
  // Add Cache Buster parameter so that fresh copy is given for the max age.
  // Cannot use cache-control since that creates CORS issues
  Math.ceil(new Date().getTime() / PET_ARTICLES_MAX_AGE);

export const transformArticles = (articles: Array<Article>): Array<PetProfileArticle> => {
  const transformed: Array<PetProfileArticle> = [];

  articles.forEach((art) => {
    transformed.push({
      title: art?.title?.rendered || '',
      image: getImage(art) || '',
      link: art.link,
    });
  });
  return transformed;
};

const loadArticles = (
  {
    queryKey: [, petType],
  }: {
    queryKey: [any, number];
  },
  injectedFetcher = fetcher,
): Promise<PetProfileArticle[]> =>
  new Promise((resolve, reject) => {
    const articleType = blogPetTypeMapping[petType];
    if (!articleType) {
      return resolve([]);
    }
    const url = `${BLOG_URL}/wp-json/wp/v2/posts?pet_type=${articleType}&_embed&tags_exclude=${EXCLUDE_TAG}&cacheBuster=${calculateCacheBusterParam()}`;
    injectedFetcher(url)
      .then((res) => res.json())
      .then((data) => resolve(transformArticles(data)))
      .catch((error) => {
        logger.error('Unable to load articles on pet profile page', 'error', 'retrieve', error);
        reject(error);
      });
  });

export const transformBeChewyArticles = (
  articles: Array<BeChewyArticle>,
): Array<PetProfileArticle> => {
  const transformed: Array<PetProfileArticle> = [];
  articles.forEach((art) => {
    transformed.push({
      title: art.title.rendered,
      image: art.featured_img_src,
      link: art.link,
    });
  });
  return transformed;
};

export const loadSpecialtyArticles = (
  {
    queryKey: [, petType],
  }: {
    queryKey: [any, number];
  },
  injectedFetcher = fetcher,
): Promise<PetProfileArticle[]> =>
  new Promise((resolve, reject) => {
    const articleType = blogSpecialtyPetTypeMapping[petType];
    if (!articleType) {
      return resolve([]);
    }
    const url = `${BLOG_URL}/wp-json/bechewy/v1/articles?pet_type=${
      blogSpecialtyPetTypeMapping[petType]
    }&img_size=thumbnail&cacheBuster=${calculateCacheBusterParam()}`;
    injectedFetcher(url)
      .then((res) => res.json())
      .then((data) => resolve(transformBeChewyArticles(data)))
      .catch((error) => {
        logger.error(
          'Unable to load specialty articles on pet profile page',
          'error',
          'retrieve',
          error,
        );
        reject(error);
      });
  });

export const loadStaticArticles = (
  {
    queryKey: [, ...posts],
  }: {
    queryKey: [any, ...number[]];
  },
  injectedFetcher = fetcher,
): Promise<Article[]> =>
  new Promise((resolve, reject) => {
    const url = `${BLOG_URL}/wp-json/wp/v2/posts?&_embed&include=${posts}`;
    injectedFetcher(url)
      .then((res) => resolve(res.json()))
      .catch((error) => {
        logger.error('Unable to load static articles', 'error', 'retrieve', error);
        reject(error);
      });
  });

export default loadArticles;
